import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import { useActivateMarkdownPlan, useCancelMarkdownPlan, useMarkdownPlan } from '../../hooks';
import { formatDateTime, formatPrice } from '../../lib/format';
import type { ToastSeverity } from '../../lib/useToast';
import { MARKDOWN_PLAN_STATUS_LABELS, markdownPlanStatusColor } from '../../model/options';

export interface MarkdownPlanDetailViewProps {
  planId: string;
  canManage: boolean;
  onBack: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function stepValueLabel(type: string, value: number): string {
  return type === 'PERCENT_OFF_BASE' ? `-${value}%` : formatPrice(value);
}

/** Markdown-plan detail — always read-only (design spec §8.9): there is no
 * `PUT .../markdown-plans/{id}` on the live BFF contract, so a DRAFT plan's steps can only be
 * viewed, never edited, after creation — only activate/cancel act on it. Rendered inline, like the
 * batch detail (no routed sub-page for plans/batches). */
export function MarkdownPlanDetailView({ planId, canManage, onBack, onResult }: MarkdownPlanDetailViewProps) {
  const { t } = useTranslation('catalog');
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const detailQuery = useMarkdownPlan(planId);
  const activateMutation = useActivateMarkdownPlan();
  const cancelMutation = useCancelMarkdownPlan();

  if (detailQuery.isLoading) {
    return <LoadingState label={t('markdownPlansPanel.loading')} />;
  }
  if (detailQuery.isError || !detailQuery.data) {
    return <ErrorState message={detailQuery.error?.message} onRetry={() => void detailQuery.refetch()} />;
  }

  const { plan, products, steps } = detailQuery.data;

  const handleActivate = () => {
    activateMutation.mutate(plan.id, {
      onSuccess: () => {
        setActivateConfirmOpen(false);
        onResult('success', t('markdownPlansPanel.activated'));
      },
      onError: (error) => onResult('error', error.message),
    });
  };

  const handleCancel = () => {
    cancelMutation.mutate(plan.id, {
      onSuccess: () => {
        setCancelConfirmOpen(false);
        onResult('success', t('markdownPlansPanel.cancelled'));
      },
      onError: (error) => onResult('error', error.message),
    });
  };

  const canCancel = canManage && (plan.status === 'DRAFT' || plan.status === 'ACTIVE');
  const canActivate = canManage && plan.status === 'DRAFT';

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>
            {t('markdownPlansPanel.backAction')}
          </Button>
          <Typography variant="subtitle1" fontWeight={700}>
            {plan.name}
          </Typography>
          <Chip size="small" color={markdownPlanStatusColor(plan.status)} label={t(MARKDOWN_PLAN_STATUS_LABELS[plan.status] ?? plan.status)} />
        </Stack>
        <Stack direction="row" spacing={1}>
          {canActivate ? (
            <Tooltip title={!canManage ? t('priceListsPanel.noManagePermissionTooltip') : ''} disableHoverListener={canManage}>
              <span>
                <Button variant="contained" disabled={!canActivate} onClick={() => setActivateConfirmOpen(true)}>
                  {t('markdownPlansPanel.activateAction')}
                </Button>
              </span>
            </Tooltip>
          ) : null}
          {canCancel ? (
            <Button variant="outlined" color="error" disabled={!canCancel} onClick={() => setCancelConfirmOpen(true)}>
              {t('markdownPlansPanel.cancelAction')}
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {t('markdownPlansPanel.productsCount', { count: products.length })}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('markdownPlansPanel.steps.seq')}</TableCell>
            <TableCell>{t('markdownPlansPanel.steps.startsAt')}</TableCell>
            <TableCell>{t('markdownPlansPanel.steps.type')}</TableCell>
            <TableCell>{t('markdownPlansPanel.steps.value')}</TableCell>
            {plan.status === 'ACTIVE' || plan.status === 'COMPLETED' ? <TableCell>{t('markdownPlansPanel.steps.materialized')}</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {steps.map((step) => (
            <TableRow key={step.seq}>
              <TableCell>{step.seq}</TableCell>
              <TableCell>{formatDateTime(step.startsAt)}</TableCell>
              <TableCell>
                {step.adjustmentType === 'PERCENT_OFF_BASE' ? t('markdownPlansPanel.steps.percentOffBase') : t('markdownPlansPanel.steps.setAmount')}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {stepValueLabel(step.adjustmentType, step.adjustmentValue)}
                </Typography>
              </TableCell>
              {plan.status === 'ACTIVE' || plan.status === 'COMPLETED' ? (
                <TableCell>
                  {step.materializedAt ? (
                    <Chip size="small" color="success" icon={<CheckCircleRoundedIcon />} label={t('markdownPlansPanel.steps.done')} />
                  ) : (
                    <Chip size="small" variant="outlined" icon={<ScheduleRoundedIcon />} label={t('markdownPlansPanel.steps.pending')} />
                  )}
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={activateConfirmOpen} onClose={activateMutation.isPending ? undefined : () => setActivateConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('markdownPlansPanel.activateConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('markdownPlansPanel.activateConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateConfirmOpen(false)} disabled={activateMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button variant="contained" onClick={handleActivate} disabled={activateMutation.isPending} startIcon={activateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {t('markdownPlansPanel.activateAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelConfirmOpen} onClose={cancelMutation.isPending ? undefined : () => setCancelConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('markdownPlansPanel.cancelConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('markdownPlansPanel.cancelConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)} disabled={cancelMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
            startIcon={cancelMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            // Visible text matches the row/detail action's own domain verb ("Cancel" the plan),
            // but its ACCESSIBLE name is distinct from the dismiss button right next to it (both
            // would otherwise announce as "Cancel" with opposite consequences — a WCAG 2.4.6
            // labelling anti-pattern this Dialog would otherwise ship).
            aria-label={t('markdownPlansPanel.cancelConfirmAction')}
          >
            {t('markdownPlansPanel.cancelAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
